/**
 * Homepage discovery rails (TES-JEV-C-APPLY CLEAR 4, X1, home v2).
 *
 * The page has one playable list with a Popular / Trending toggle under the
 * hero. Hero stays most-downloaded. Popular is the next downloads down, so the
 * hero's five sounds are not repeated. Trending is the 7-day download velocity
 * from `trending_sounds` with the hero and Popular sounds removed, so the two
 * views never show the same row.
 *
 * There is no New view. Sounds arrive in ingest batches: on 2026-10-02 the
 * newest 60 were all added on 2026-09-28, two thirds with zero downloads and
 * titles like "Coin 3". A date-sorted list on the homepage is filler however
 * its rows are filtered.
 */

import { type Sound } from '@/lib/supabase-types'

export const HERO_ROTATION_SIZE = 5
export const HOMEPAGE_LIST_SIZE = 8
/** The popularity query must reach past the hero to fill the Popular list. */
export const HOMEPAGE_POPULAR_POOL = HERO_ROTATION_SIZE + HOMEPAGE_LIST_SIZE
/**
 * The top of the velocity table overlaps the hero and Popular almost entirely
 * (10 of the top 13 on 2026-10-02), so the pool reaches well past them.
 */
export const HOMEPAGE_TRENDING_POOL = 30
/** Below this many distinct rows the Trending view is not worth a toggle. */
export const HOMEPAGE_TRENDING_MIN = 4
export const MORE_TRENDING_HREF = '/sounds/category/trending'

export function soundIdSet(sounds: readonly { id: string }[]): Set<string> {
  return new Set(sounds.map(sound => sound.id))
}

/** Receives a downloads-desc pool; an optional seasonal pin takes the first slide. */
export function selectHeroSounds(
  sounds: readonly Sound[],
  pinned?: Sound | null,
): Sound[] {
  const downloaded = sounds.filter(sound => (sound.downloads ?? 0) > 0)
  const source = downloaded.length > 0 ? downloaded : sounds
  if (!pinned) return source.slice(0, HERO_ROTATION_SIZE)
  const rest = source.filter(sound => sound.id !== pinned.id)
  return [pinned, ...rest.slice(0, HERO_ROTATION_SIZE - 1)]
}

/**
 * Each sound's download rank in `ranked`, a downloads-desc list from the top of
 * the catalog: 1 plus the number of sounds with strictly more downloads, so a
 * tie shares a rank. Sounds with no downloads get no rank.
 */
export function downloadRanks(
  ranked: readonly Sound[],
  sounds: readonly Sound[],
): Record<string, number> {
  const ranks: Record<string, number> = {}
  for (const sound of sounds) {
    const downloads = sound.downloads ?? 0
    if (downloads <= 0) continue
    ranks[sound.id] = 1 + ranked.filter(other => (other.downloads ?? 0) > downloads).length
  }
  return ranks
}

const RANK_PHRASES = ['the most', 'the second most', 'the third most', 'the fourth most', 'the fifth most']

/** "the most on the site", "the second most on the site", up to fifth. */
export function downloadRankPhrase(rank: number | undefined): string | null {
  const phrase = rank ? RANK_PHRASES[rank - 1] : undefined
  return phrase ? `${phrase} on the site` : null
}

export function selectPopularRail(input: {
  pool: readonly Sound[]
  excludeIds: ReadonlySet<string>
  limit?: number
}): Sound[] {
  const limit = input.limit ?? HOMEPAGE_LIST_SIZE
  if (limit <= 0) return []

  const picked: Sound[] = []
  const pickedIds = new Set<string>()
  for (const sound of input.pool) {
    if (picked.length >= limit) break
    if (input.excludeIds.has(sound.id) || pickedIds.has(sound.id)) continue
    picked.push(sound)
    pickedIds.add(sound.id)
  }
  return picked
}

export function resolveHomepageRails(input: {
  popular: readonly Sound[]
  trendingPool: readonly Sound[]
  pinnedHero?: Sound | null
}): {
  hero: Sound[]
  heroRanks: Record<string, number>
  popular: Sound[]
  trending: Sound[]
} {
  const hero = selectHeroSounds(input.popular, input.pinnedHero)
  const heroIds = soundIdSet(hero)
  const popular = selectPopularRail({ pool: input.popular, excludeIds: heroIds })
  const trending = selectPopularRail({
    pool: input.trendingPool,
    excludeIds: soundIdSet([...hero, ...popular]),
  })
  return {
    hero,
    heroRanks: downloadRanks(input.popular, hero.filter(sound => sound.id !== input.pinnedHero?.id)),
    popular,
    trending: trending.length >= HOMEPAGE_TRENDING_MIN ? trending : [],
  }
}
